/**
 * FeatureDomainSidebar — the left-rail domain picker.
 *
 * Lists every feature domain with its feature count; the active domain gets an
 * accent highlight. Extracted from FeatureManagerWindow; behavior-preserving.
 */
import React from 'react';
import {
  COLORS,
  SPACE,
  RADII,
  FONT_SIZE,
  FONT_WEIGHT,
  DURATION,
  EASING,
} from '../../theme';
import { Badge } from '../../primitives';
import { featureFlags } from '../../../../core/features';

export interface FeatureDomainSidebarProps {
  domains: string[];
  selectedDomain: string;
  onSelect: (domain: string) => void;
}

const sidebarStyle: React.CSSProperties = {
  width: 220,
  borderRight: `1px solid ${COLORS.borderSubtle}`,
  padding: SPACE.xl,
  overflowY: 'auto',
};

const sidebarTitleStyle: React.CSSProperties = {
  fontSize: FONT_SIZE.sm,
  fontWeight: FONT_WEIGHT.semibold,
  color: COLORS.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: SPACE.lg,
  paddingLeft: SPACE.md,
};

function domainButtonStyle(active: boolean): React.CSSProperties {
  return {
    width: '100%',
    padding: `${SPACE.md}px ${SPACE.lg}px`,
    marginBottom: SPACE.xs,
    borderRadius: RADII.lg,
    border: 'none',
    background: active ? COLORS.surfaceRaised : 'transparent',
    color: active ? COLORS.info : COLORS.textSecondary,
    fontSize: FONT_SIZE.md,
    textAlign: 'left',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACE.sm,
    transition: `all ${DURATION.fast} ${EASING.standard}`,
  };
}

export const FeatureDomainSidebar: React.FC<FeatureDomainSidebarProps> = ({
  domains,
  selectedDomain,
  onSelect,
}) => {
  return (
    <div style={sidebarStyle}>
      <div style={sidebarTitleStyle}>Domains</div>
      {domains.map((domain) => {
        const domainCfg = featureFlags.getDomainConfig(domain);
        const isActive = domain === selectedDomain;
        const featureCount = domainCfg ? Object.keys(domainCfg.features).length : 0;

        return (
          <button
            key={domain}
            type="button"
            style={domainButtonStyle(isActive)}
            onClick={() => onSelect(domain)}
          >
            <span>{domainCfg?.name || domain}</span>
            <Badge tone={isActive ? 'info' : 'neutral'}>{featureCount}</Badge>
          </button>
        );
      })}
    </div>
  );
};
